import type { Dispatch, Ref, SetStateAction } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData as CanvasNodeData } from "../OperationCanvasNode.js";
import { NODE_LABELS } from "../../lib/operationPorts.js";
import type { QueryResultsPayload } from "../QueryResultsGrid.js";
import { NodeMetadataFields, NodeRunHistory, type NodeMetadata, type NodeOutputRecord, type NodeRunHistoryEntry } from "./nodeInspectorWidgets.js";
import { TransformInspectorWorkbench, type TenantAiModelOption } from "./TransformInspectorWorkbench.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { InspectorSlot } from "../inspector/inspectorModel.js";

export type TransformChatMessage = { id: string; role: "user" | "assistant"; text: string };

export type TransformLayerStats = { runAt: string | null; durationMs: number | null; rowCount: number | null };

type OperationStatus = "draft" | "invalid" | "published" | "paused" | "validate";

export function OperationInspectorTransform({
  selectedNode,
  operationMeta,
  transformActiveSlot,
  setTransformActiveSlot,
  getDirectParentNodes,
  nodeOutputs,
  setNodeData,
  operationStatus,
  transformForceRefresh,
  setTransformForceRefresh,
  onRunTransformPreview,
  transformRunBusy,
  transformSending,
  onResetTransformChat,
  transformRunError,
  transformInteractionMode,
  setTransformInteractionMode,
  tenantAiModels,
  selectedAiModelId,
  onTenantAiModelChange,
  aiChatBlocked,
  transformChatScrollRef,
  transformMessages,
  transformPrompt,
  setTransformPrompt,
  onSendTransformPrompt,
  onCancelTransformChat,
  transformSql,
  onTransformSqlChange,
  transformJson,
  onTransformJsonChange,
  onTransformSpecBlur,
  transformJsonError,
  transformPreview,
  transformStats,
  transformSubTab,
  setTransformSubTab,
  nodeCacheLoadingThisNode,
  nodeOutputRunAt,
  nodeRunHistory,
  nodeRunHistoryLoading,
  onSelectHistoryRun,
  onOpenTrace,
  columnOverrides,
  onColumnDomainChange,
  retypeBusy
}: {
  selectedNode: Node<CanvasNodeData>;
  operationMeta: NodeMetadata;
  /** ADR-0129 — controlled spine slot (editor-owned so a completed run can jump to Data). */
  transformActiveSlot: InspectorSlot;
  setTransformActiveSlot: (slot: InspectorSlot) => void;
  getDirectParentNodes: (nodeId: string) => Node<CanvasNodeData>[];
  nodeOutputs: Record<string, NodeOutputRecord>;
  setNodeData: (nodeId: string, updater: (data: CanvasNodeData) => CanvasNodeData) => void;
  operationStatus: OperationStatus;
  transformForceRefresh: boolean;
  setTransformForceRefresh: Dispatch<SetStateAction<boolean>>;
  onRunTransformPreview: () => void;
  transformRunBusy: boolean;
  transformSending: boolean;
  onResetTransformChat: () => void;
  transformRunError: string | null;
  transformInteractionMode: "ask" | "agent";
  setTransformInteractionMode: Dispatch<SetStateAction<"ask" | "agent">>;
  tenantAiModels: TenantAiModelOption[];
  selectedAiModelId: string;
  onTenantAiModelChange: (modelId: string) => void;
  aiChatBlocked: boolean;
  transformChatScrollRef: Ref<HTMLDivElement>;
  transformMessages: TransformChatMessage[];
  transformPrompt: string;
  setTransformPrompt: Dispatch<SetStateAction<string>>;
  onSendTransformPrompt: (message: string, onRestore: (msg: string) => void) => void;
  onCancelTransformChat: () => void;
  transformSql: string;
  onTransformSqlChange: (value: string) => void;
  transformJson: string;
  onTransformJsonChange: (value: string) => void;
  onTransformSpecBlur: () => void;
  transformJsonError: string | null;
  transformPreview: QueryResultsPayload | null;
  transformStats: TransformLayerStats | null;
  transformSubTab: "data" | "schema";
  setTransformSubTab: Dispatch<SetStateAction<"data" | "schema">>;
  nodeCacheLoadingThisNode: boolean;
  nodeOutputRunAt: string | null | undefined;
  nodeRunHistory: NodeRunHistoryEntry[];
  nodeRunHistoryLoading: boolean;
  onSelectHistoryRun: (run: NodeRunHistoryEntry) => void;
  /** ADR-0128 — open a run's full-chain Trace from a History row. */
  onOpenTrace?: (runId: string) => void;
  /** ADR-0147 — Domain override control on the Schema subtab's Output panel. */
  columnOverrides?: Record<string, string>;
  onColumnDomainChange?: (columnName: string, domain: import("../ColumnDomainControl.js").ColumnDomain | null) => void;
  retypeBusy?: boolean;
}) {
  // ADR-0130: the input side of the schema mapping (shown in the Data tab's
  // Schema subtab) — each direct upstream node's landed output columns.
  const inputSchemas = getDirectParentNodes(selectedNode.id).map((pn) => ({
    nodeId: pn.id,
    label: pn.data.label || NODE_LABELS[pn.data.nodeType],
    schema: nodeOutputs[pn.id]?.outputSchema ?? null,
  }));

  const workbenchProps = {
    operationStatus,
    inputSchemas,
    transformForceRefresh,
    setTransformForceRefresh,
    onRunTransformPreview,
    transformRunBusy,
    transformSending,
    onResetTransformChat,
    transformRunError,
    transformInteractionMode,
    setTransformInteractionMode,
    tenantAiModels,
    selectedAiModelId,
    onTenantAiModelChange,
    aiChatBlocked,
    transformChatScrollRef,
    transformMessages,
    transformPrompt,
    setTransformPrompt,
    onSendTransformPrompt,
    onCancelTransformChat,
    transformSql,
    onTransformSqlChange,
    transformJson,
    onTransformJsonChange,
    onTransformSpecBlur,
    transformJsonError,
    transformPreview,
    transformStats,
    transformSubTab,
    setTransformSubTab,
    nodeCacheLoadingThisNode,
    nodeOutputRunAt,
    columnOverrides,
    onColumnDomainChange,
    retypeBusy
  };

  const infoContent = (
    <div style={{ display: "grid", gap: 8, padding: "4px 0" }}>
      <div className="field">
        <label className="label">Node label</label>
        <input
          className="input"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, label: e.target.value }))}
          placeholder="Unnamed"
        />
      </div>
      <div className="field">
        <label className="label">Description</label>
        <textarea
          className="textarea"
          rows={3}
          placeholder="Optional description for this node…"
          value={selectedNode.data.description ?? ""}
          onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, description: e.target.value }))}
        />
      </div>
      <NodeMetadataFields meta={operationMeta} />
    </div>
  );

  const setupContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <TransformInspectorWorkbench section="editor" {...workbenchProps} />
    </div>
  );

  return (
    <InspectorTabs
      surfaceId="operationEditor"
      tabGroupId="transform"
      activeSlot={transformActiveSlot}
      onSlotChange={setTransformActiveSlot}
      tabs={[
        { slot: "info", content: infoContent },
        { slot: "setup", content: setupContent },
        { slot: "data", content: <TransformInspectorWorkbench section="preview" {...workbenchProps} /> },
        { slot: "ai", fill: true, content: <TransformInspectorWorkbench section="chat" {...workbenchProps} /> },
        {
          slot: "runs",
          content: (
            <NodeRunHistory
              runs={nodeRunHistory}
              loading={nodeRunHistoryLoading}
              onSelectRun={onSelectHistoryRun}
              onOpenTrace={onOpenTrace}
            />
          )
        }
      ]}
    />
  );
}
